import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * Product enquiry dialog with a contact form and variant-aware hidden fields.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 *
 * @extends {DialogComponent}
 */
class ProductEnquiryComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    if (this.querySelector('[data-enquiry-success], [data-enquiry-error]')) {
      queueMicrotask(() => this.showDialog());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /** @type {Element | null} */
  #section = null;

  /**
   * @param {import('./events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const productId = this.dataset.productId;
    if (!productId) return;

    if (event.detail.data.newProduct) {
      this.dataset.productId = String(event.detail.data.newProduct.id);
      this.#setField('product_id', event.detail.data.newProduct.id);
      this.#setField('product_title', event.detail.data.newProduct.title ?? this.#getField('product_title'));
      if (event.detail.data.newProduct.url) {
        this.#updateProductUrl(event.detail.data.newProduct.url, event.detail.resource?.id);
      }
    } else if (String(event.detail.data.productId) !== String(productId)) {
      return;
    }

    const variant = event.detail.resource;
    if (!variant) return;

    this.#setField('variant_id', variant.id ?? '');
    this.#setField('variant_title', variant.title ?? variant.name ?? '');
    this.#setField('sku', variant.sku ?? '');
    this.#updateProductUrl(this.#getField('product_url'), variant.id);

    const summary = this.querySelector('[data-enquiry-product-summary]');
    if (summary) {
      const title = this.#getField('product_title');
      const variantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : '';
      summary.textContent = variantTitle ? `${title} — ${variantTitle}` : title;
    }
  };

  /**
   * @param {string} name
   * @param {string | number} value
   */
  #setField(name, value) {
    const input = this.querySelector(`input[name="contact[${name}]"]`);
    if (input instanceof HTMLInputElement) {
      input.value = String(value ?? '');
    }
  }

  /**
   * @param {string} name
   * @returns {string}
   */
  #getField(name) {
    const input = this.querySelector(`input[name="contact[${name}]"]`);
    return input instanceof HTMLInputElement ? input.value : '';
  }

  /**
   * @param {string} baseUrl
   * @param {string | number | undefined} variantId
   */
  #updateProductUrl(baseUrl, variantId) {
    if (!baseUrl) return;

    try {
      const url = new URL(baseUrl, window.location.origin);
      if (variantId) {
        url.searchParams.set('variant', String(variantId));
      }
      this.#setField('product_url', `${url.pathname}${url.search}`);
    } catch {
      this.#setField('product_url', baseUrl);
    }
  }
}

if (!customElements.get('product-enquiry-component')) {
  customElements.define('product-enquiry-component', ProductEnquiryComponent);
}
